<template>
	<CDialog
		:visible="visible"
		title="DNS Configuration Instructions"
		custom-class="email-domain-settings-dialog dialog--medium"
		@done="$emit('close')"
	>
		<p>
			You'll need to configure your website domain for use with Cognito Forms system emails.
			<a href="/support/244/building-forms/verified-email-domains" target="_blank">Read these instructions</a> to do it yourself.
			Or, <a class="copy-dns">copy</a> the information below and send it to your website administrator if he or she will be making these changes.
		</p>

		<table id="domain-settings-table" class="multiheader-table">
			<caption class="safari-bug-workaround">
				<div class="safari-bug-workaround__div">
					DNS Information
					<button class="copy-dns copy-button" title="Copy DNS Information" @click="copyToClipboard">
						<span class="copied-message" :class="{ 'copied': copied }">
							Copied!
						</span><i class="icon-paper-clip"></i>
					</button>
				</div>
			</caption>

			<tbody>
				<tr>
					<th colspan="2">SPF Record</th>
				</tr>
				<tr>
					<th>Host</th>
					<th><code>{{ spfRecordHost }}</code></th>
				</tr>
				<tr>
					<th>Type</th>
					<th><code>TXT</code></th>
				</tr>
				<tr>
					<th>Value</th>
					<th><code>{{ domain.Spf }}</code></th>
				</tr>
			</tbody>

			<tbody v-for="provider in domain.Providers" :key="provider.Name">
				<tr>
					<th colspan="2">{{ provider.Name + " DKIM" }}</th>
				</tr>
				<tr>
					<th>Host</th>
					<th><code>{{ provider.DkimHost }}</code></th>
				</tr>
				<tr>
					<th>Type</th>
					<th><code>TXT</code></th>
				</tr>
				<tr>
					<th>Value</th>
					<th><code>{{ provider.Dkim }}</code></th>
				</tr>
			</tbody>
		</table>

		<template #footer>
			<button
				class="c-modal-button c-modal-button-cancel button--secondary"
				@click="$emit('close')"
			>
				Close
			</button>
		</template>
	</CDialog>
</template>

<script lang="ts">
	import { defineComponent, ref, computed } from 'vue';
	import type { PropType } from 'vue';
	import type { EmailDomain } from '@cognitoforms/types/server-types/model/email-domain';
	import CDialog from 'src/components/dialogs/Dialog.vue';

	export default defineComponent({
		name: 'CEmailDomainSettingsDialog',
		components: {
			CDialog
		},
		props: {
			visible: { type: Boolean, default: false },
			domain: { type: Object as PropType<EmailDomain>, required: true }
		},
		emit: ['close'],
		setup(props) {
			const copied = ref(false);
			const spfRecordHost = computed(() => {
				return props.domain.Id.split('.').length > 2 ? props.domain.Id : '@';
			});
			const markdown = computed(() => {
				let md = `## SPF Record\nHost: ${spfRecordHost.value}\nType: TXT\nValue: ${props.domain.Spf}`;
				for (const provider of props.domain.Providers) {
					md += `\n---\n## ${provider.Name} DKIM\nHost: ${provider.DkimHost}\nType: TXT\nValue: ${provider.Dkim}`;
				}
				return md;
			});

			return {
				copied,
				spfRecordHost,
				markdown,
				copyToClipboard: async () => {
					try {
						await navigator.clipboard.writeText(markdown.value);
						copied.value = true;
						setTimeout(() => {
							copied.value = false;
						}, 3000);
					}
					catch {
					}
				}
			};
		}
	});
</script>

<style lang="scss">
.email-domain-settings-dialog {

	code {
		display: block;
		word-break: break-all;
		text-align: left;
	}

	.multiheader-table {
		width: 100%;
		border-spacing: 0;
	}

	.multiheader-table tbody {
		border: 1px solid var(--border-table);
	}

	.multiheader-table caption {
		padding: $space__3;
		font-weight: $weight__text--bold;
	}

	.multiheader-table th[colspan] {
		background: var(--bg-th);
		text-align: left;
	}

	.multiheader-table th {
		font-weight: $weight__text--medium;
		text-align: left;
	}

	.multiheader-table th:not([colspan]):first-child {
		padding-left: $space__4;
	}

	.multiheader-table th,
	td {
		padding: $space__3;
	}

	.multiheader-table tr:nth-child(odd) {
		background: var(--shade-2);
	}
}
</style>